export const createCard = (
    macAddress, 
    sensorDescription, 
    cardImage, 
    mostRecentBattery, 
    mostRecentTemp, 
    mostRecentHumidity, 
    mostRecentTimestamp,
    mostRecentWater = '',
    addIrrigation = "none",
    humidityHigh = '',
    humidityLow = '',
    temperatureHigh = '',
    temperatureLow = '',
    waterHigh = '',
    waterLow = '',
    lightLow = '',
    lightHigh = '',
    water2High = '',
    water2Low = '',
    ) => {
    return {
        macAddress: macAddress,
        sensorDescription: sensorDescription,
        addIrrigation: addIrrigation,
        cardImage: cardImage,
        humidityHigh: humidityHigh,
        humidityLow: humidityLow ,
        temperatureHigh: temperatureHigh ,
        temperatureLow:  temperatureLow ,
        waterHigh: waterHigh,
        waterLow: waterLow,
        water2High: water2High,
        water2Low: water2Low,
        mostRecentWater: mostRecentWater,
        mostRecentBattery: mostRecentBattery,
        mostRecentTemp: mostRecentTemp,
        mostRecentHumidity: mostRecentHumidity,
        mostRecentTimestamp: mostRecentTimestamp,
        lightLow: lightLow,
        lightHigh: lightHigh,
    }   
}

const fieldMapping = {
    mac_address: "macAddress",
    sensor_description: "sensorDescription",
    add_irrigation: "addIrrigation",
    card_image: "cardImage",
    humidity_high: "humidityHigh",
    humidity_low: "humidityLow",
    temperature_high: "temperatureHigh",
    temperature_low: "temperatureLow",
    water_high: "waterHigh",
    water_low: "waterLow",
    water2_high: "water2High",
    water2_low: "water2Low",
    most_recent_water: "mostRecentWater",
    most_recent_battery: "mostRecentBattery",
    most_recent_temp: "mostRecentTemp",
    most_recent_humidity: "mostRecentHumidity",
    most_recent_timestamp: "mostRecentTimestamp",
    light_low: "lightLow",
    light_high: "lightHigh"
}

export const checkMqttCard = (macAddress) => {
    if (!macAddress) {
        return false;
    }
    if (macAddress.slice(0,2) === "MQ") {
        return true;
    }
    return false;
}

/**
 * Device classes are distinguished by a MAC-address prefix:
 *  - "MQ" → MQTT / Wi-Fi sensors streaming real-time data
 *  - "LR" → discrete / LoRa sensors that report on a slow cadence (e.g. hourly),
 *           typically outdoors (weather stations, in-ground soil-moisture probes).
 *  - anything else → legacy / standard card
 */
export const DEVICE_TYPES = {
    MQTT: 'mqtt',
    DISCRETE: 'discrete',
    STANDARD: 'standard',
};

// Prefix for discrete / LoRa devices. Mirrors the "MQ" convention for MQTT.
export const DISCRETE_MAC_PREFIX = 'LR';

export const checkDiscreteCard = (macAddress) => {
    if (!macAddress) {
        return false;
    }
    return macAddress.slice(0, 2).toUpperCase() === DISCRETE_MAC_PREFIX;
};

export const getCardDeviceType = (macAddress) => {
    if (checkDiscreteCard(macAddress)) {
        return DEVICE_TYPES.DISCRETE;
    }
    if (checkMqttCard(macAddress)) {
        return DEVICE_TYPES.MQTT;
    }
    return DEVICE_TYPES.STANDARD;
};

// Human-readable cadence label shown on discrete cards/detail.
export const DISCRETE_UPDATE_CADENCE = 'Hourly';

/** Options for the Add Sensor type selector (CardModal). */
export const SENSOR_TYPE_OPTIONS = [
    {
        id: DEVICE_TYPES.MQTT,
        label: 'MQTT / Wi-Fi sensor',
        shortLabel: 'MQTT / Wi-Fi',
        description: 'Real-time indoor sensor over MQTT — temperature, humidity, soil moisture, light, and water level.',
        macPrefix: 'MQ',
        icon: 'ri-wifi-line',
        cadence: 'Real-time (~5s)',
    },
    {
        id: DEVICE_TYPES.DISCRETE,
        label: 'LoRa / discrete sensor',
        shortLabel: 'LoRa / discrete',
        description: 'Outdoor or in-ground sensor with periodic uplinks — soil moisture, temperature, battery. Ideal for lawns, weather stations, and sprinkler zones.',
        macPrefix: DISCRETE_MAC_PREFIX,
        icon: 'ri-base-station-line',
        cadence: DISCRETE_UPDATE_CADENCE,
    },
];

export const getSensorTypeDefinition = (typeId) =>
    SENSOR_TYPE_OPTIONS.find((option) => option.id === typeId) ?? SENSOR_TYPE_OPTIONS[0];

export const macMatchesSensorType = (macAddress, typeId) => {
    if (!macAddress) {
        return false;
    }
    const definition = getSensorTypeDefinition(typeId);
    return macAddress.slice(0, 2).toUpperCase() === definition.macPrefix;
};

/** Seed MAC input with the correct prefix when the user picks a sensor type. */
export const buildMacPrefixForSensorType = (typeId) => {
    const definition = getSensorTypeDefinition(typeId);
    return `${definition.macPrefix}:`;
};

export const applySensorTypeToMacAddress = (macAddress, typeId) => {
    const prefix = buildMacPrefixForSensorType(typeId);
    if (!macAddress) {
        return prefix;
    }
    // Replace an existing MQ/LR prefix if the user switches types.
    if (/^(MQ|LR):/i.test(macAddress)) {
        return prefix + macAddress.slice(3);
    }
    return prefix + macAddress;
};

/**
 * MOCK: deterministic-ish hourly readings for a discrete/LoRa sensor.
 *
 * Until the backend ingests LoRa uplinks, the discrete detail view runs on
 * generated data so we can mock the full experience (soil moisture, temp,
 * battery drain, last-seen). Values are seeded off the MAC so a given device
 * looks stable across renders within a session.
 *
 * @param {string} macAddress
 * @param {number} hours - how many hourly points to generate (default 24)
 * @returns {Array<{ timestamp: string, soilMoisture: number, temperature: number, battery: number }>}
 */
export const generateMockDiscreteReadings = (macAddress, hours = 24) => {
    const seed = (macAddress || 'LR')
        .split('')
        .reduce((acc, ch) => acc + ch.charCodeAt(0), 0);

    const readings = [];
    const now = Date.now();
    for (let i = hours - 1; i >= 0; i -= 1) {
        const t = new Date(now - i * 60 * 60 * 1000);
        // Smooth-ish daily curves using the hour of day + seed offset.
        const hourOfDay = t.getHours();
        const wave = Math.sin(((hourOfDay + (seed % 12)) / 24) * Math.PI * 2);

        const soilMoisture = Math.round((42 + wave * 18 + ((seed + i) % 5)) * 10) / 10; // ~24–62 %
        const temperature = Math.round((64 + wave * 14 + ((seed + i) % 4)) * 10) / 10;  // ~54–82 °F
        const battery = Math.round((4.15 - (hours - 1 - i) * 0.004) * 100) / 100;       // slow drain

        readings.push({
            timestamp: t.toISOString(),
            soilMoisture: Math.max(0, Math.min(100, soilMoisture)),
            temperature,
            battery,
        });
    }
    return readings;
};

export const mapFieldsToCamelCase = (data) => {
    if (!data) {
        return [];
    }

    if (Array.isArray(data)) {
        //For each item, map its field.
        return data.map((item) => mapFieldsToCamelCase(item));
    }

    //Collect the results into a new array.
    const mappedData = {};
    for (const [dbField, frontendField] of Object.entries(fieldMapping)) {
        if(dbField in data) {
            //Use frontendField as the new key (camelCase) and store the data from old key.
            mappedData[frontendField] = data[dbField];
        }
    }
    return mappedData;
}

//Any app image related operations will go here.

// Card images are only ever rendered as small thumbnails (~150px), yet phones
// produce multi-MB photos. Storing them raw bloats the /api/cards payload (each
// image is base64-inlined per row), which made the cards fetch take ~30s to
// download. Always downscale + re-encode before saving so the payload stays small.
const CARD_IMAGE_COMPRESSION_OPTIONS = {
    maxSizeMB: 0.25,
    maxWidthOrHeight: 800,
    useWebWorker: true,
    initialQuality: 0.7,
    fileType: 'image/jpeg',
};

// Compresses a user-selected image File down to a thumbnail-sized JPEG.
// Returns the compressed File (or the original if compression throws, so an
// upload never hard-fails purely because of compression).
export const compressCardImage = async (file) => {
    if (!file) {
        return file;
    }
    try {
        // Imported lazily so the (large) compression lib isn't pulled into the
        // initial bundle and so callers can stay synchronous-ish.
        const { default: imageCompression } = await import('browser-image-compression');
        return await imageCompression(file, CARD_IMAGE_COMPRESSION_OPTIONS);
    } catch (ex) {
        console.error('Image compression failed: ', ex);
        return file;
    }
};

// Image validation utility:
export const validateImage = (base64Image) => {
    //Check the file type:
    const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
    const fileType = base64Image.split(';')[0].split(':')[1];

    //Check the file size:
    const fileSize = (base64Image.length * 3) / 4; //Approximate base64 size.
    const maxSize = 5 * 1024 * 1024; //5MB

    return validTypes.includes(fileType) && fileSize < maxSize;
}

export function getLevelColor (metric, min, max) {
    //in case metric exceeds the range.
    const clampedValue = Math.max(min, Math.min(metric, max));

    //Compute hue based on temperature (0 = blue, 240 = red)
    const hue = 200 - ((clampedValue - min) / (max - min)) * 200;

    //100% saturation, 50% brightness:
    return `hsl(${hue}, 90%, 55%)`;
}